import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { CtaBand } from "@/components/CtaBand";
import { EASE, Reveal } from "@/components/Reveal";
import { SERVICES } from "@/data/content";

export default function Services() {
  const [active, setActive] = useState(SERVICES[0].id);
  const svc = SERVICES.find((s) => s.id === active);

  return (
    <div data-testid="services-page">
      <Seo
        title="Services — Vigarbiz"
        siteName="Vigarbiz"
        description="Six growth engines under one roof: SEO & organic growth, paid media, social, content & creative, web engineering, and analytics & CRO."
      />
      <PageHeader
        overline="Services"
        lines={["Six engines.", <span key="o" className="text-brand">One growth</span>, "Machine."]}
        description="Every service is run as a P&L, not a deliverable list. Pick an engine to see what ships, what it moves and how we prove it."
      />

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[360px_1fr] lg:gap-10">
          <Reveal className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0">
            {SERVICES.map((s) => (
              <button
                key={s.id}
                data-testid={`service-tab-${s.id}`}
                onClick={() => setActive(s.id)}
                className={`flex shrink-0 items-center gap-4 rounded-xl border px-5 py-4 text-left transition-colors duration-300 lg:w-full ${
                  active === s.id
                    ? "border-brand/60 bg-surface text-white"
                    : "border-white/10 bg-transparent text-muted2 hover:border-white/25 hover:text-white"
                }`}
              >
                <span className={`font-mono text-xs ${active === s.id ? "text-brand" : "text-dim"}`}>{s.num}</span>
                <span className="font-display text-sm font-bold sm:text-base">{s.title}</span>
                <ArrowUpRight
                  className={`ml-auto hidden h-4 w-4 lg:block ${active === s.id ? "text-brand" : "text-dim"}`}
                />
              </button>
            ))}
          </Reveal>

          <AnimatePresence mode="wait">
            <motion.div
              key={svc.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="rounded-2xl border border-white/10 bg-surface p-6 sm:p-10 lg:p-12"
              data-testid="service-detail"
            >
              <div className="flex items-start justify-between gap-6">
                <div>
                  <svc.icon className="h-10 w-10 text-brand" strokeWidth={1.5} />
                  <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                    {svc.title}
                  </h2>
                  <p className="mt-2 font-display text-lg text-brand">{svc.tagline}</p>
                </div>
                <span className="hidden font-display text-7xl font-black text-white/10 lg:block">{svc.num}</span>
              </div>

              <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted2 md:text-lg">{svc.description}</p>

              <div className="mt-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-dim">What ships</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {svc.deliverables.map((d) => (
                    <span
                      key={d}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-elevated px-4 py-2 text-xs text-white/80"
                    >
                      <Check className="h-3 w-3 text-volt" />
                      {d}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-10 grid gap-6 sm:grid-cols-3">
                {svc.kpis.map((k) => (
                  <div key={k.label}>
                    <div className="flex items-baseline justify-between">
                      <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted2">{k.label}</span>
                      <span className="font-mono text-sm font-bold text-volt">{k.delta}</span>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${k.value}%` }}
                        transition={{ duration: 1, delay: 0.25, ease: EASE }}
                        className="h-full rounded-full bg-gradient-to-r from-brand to-volt"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      <CtaBand sub="Not sure which engine you need? That's what the first call is for — we'll map your funnel and tell you plainly." />
    </div>
  );
}
